import { useEffect, useMemo, useState } from 'react';
import { ClpSdk, Fetcher, FetcherApi, JsonFetcher, createLocalizationClient } from '@canvas-logic/clp-sdk';

import { uiFallBack, view3dFallBack } from '@/constants';
import { Sdk } from '@/types';
import { AppConfig } from '@/config';
import { useConfiguratorRoute } from '@/hooks/useConfiguratorRoute';

import { Configurator } from './Configurator';
import { ConfiguratorContext, ConfiguratorSettings } from './ConfiguratorContext';

import { useLanguageSwitcher } from '@/hooks/useLanguageSwitcher';
import { ResolvedLanguage } from '@/localization/ResolvedLanguage';
import { LanguageFallbackGuard } from '@/localization/LanguageFallbackGuard';
import { effectiveRequestedLanguage, type LanguageIssue } from '@/localization/languageFallback';
import { LocalizationProvider, DisplayContextProvider } from '@canvas-logic/clp-shared-ui';
import type { DisplayContext } from '@canvas-logic/clp-localization';
import { IConfiguratorCatalogProductApiOptions } from '@canvas-logic/clp-api-types';
import type { View3dConfigurationDto } from '@canvas-logic/clp-viewer3d';

export enum Source {
  Local,
  Bucket,
  Backend,
}

interface IProps {
  source: Source;
  composite: boolean;
}

// Basic Configurator supplies systemValue at every call site from already
// loaded product data, so the expensive `system` layer is omitted, and it
// declares no custom:* keys (ADR-0075 Resolved decision 4). The result is
// content-equivalent to the legacy two-fetch resolver — the parity anchor
// for this migration.
const TRANSLATION_FAMILIES = ['entities', 'ui'] as const;
const TRANSLATION_LAYERS = ['selected', 'default'] as const;

export const ProductConfigurator = ({ source, composite }: IProps) => {
  const { requestedLanguage } = useLanguageSwitcher();
  const { draft, catalogId, projectId, productId, link, configurationId, hideConfigurationControls, hide3d } =
    useConfiguratorRoute();
  const productIdNum = typeof productId === 'number' ? productId : Number(productId) || undefined;
  // One client for the component's lifetime: the provider refetches on client
  // identity change (phase-5 review watch item), and translations always come
  // from the backend server regardless of the asset `source`.
  const localizationClient = useMemo(() => createLocalizationClient(new Fetcher(AppConfig.serverUrl)), []);
  const useLive = draft || catalogId == null;
  // Mirrors the old query-hook guards: no server URL, no product, or a live
  // read without a project → don't mount the provider; hooks then serve
  // system values exactly as the old null blob did.
  const canLoadTranslations = Boolean(AppConfig.serverUrl) && productIdNum != null && (!useLive || projectId != null);
  const params: IConfiguratorCatalogProductApiOptions = {
    version: draft ? 'draft' : 'active',
    // Absent for live (non-catalog) reads — that is exactly what `useLive` tests for
    // above. The DTO still declares `catalogId` required, so pass the runtime value
    // through unchanged rather than inventing one.
    catalogId: catalogId as number,
    projectId,
    productId,
    configurationId,
  };
  const [state, setState] = useState<ConfiguratorSettings | null>(null);
  const [loadConfigurationError, setLoadConfigurationError] = useState<{ code: string; cause?: string } | null>(null);
  // A `?lang=` the platform will not serve; recorded here so the request can
  // be re-issued without it.
  const [languageIssue, setLanguageIssue] = useState<LanguageIssue | null>(null);
  const languageDebugContext = useMemo(
    () => ({ projectId, catalogId, productId: productIdNum, draft }),
    [projectId, catalogId, productIdNum, draft],
  );

  useEffect(() => {
    // fetch('https://storage.googleapis.com/clp-basic-configurator/assets/acer-kiosk/configs/ui_specification.json')
    //   .then((res) => res.json())
    //   .then((a) => console.log('bucket', a));

    let fetcherBaseUrl = '';
    let _assetsBaseUrl = '';
    let fetcher: FetcherApi;
    switch (source) {
      case Source.Local:
        fetcherBaseUrl = `./assets/${projectId}`;
        _assetsBaseUrl = fetcherBaseUrl;
        fetcher = new JsonFetcher(fetcherBaseUrl);
        break;
      case Source.Bucket:
        fetcherBaseUrl = `https://storage.googleapis.com/${projectId}`;
        _assetsBaseUrl = fetcherBaseUrl;
        fetcher = new JsonFetcher(fetcherBaseUrl);
        break;
      case Source.Backend:
        fetcherBaseUrl = AppConfig.serverUrl;
        _assetsBaseUrl = '';
        fetcher = new Fetcher(fetcherBaseUrl);
        break;
      default:
        throw new Error(`Invalid source ${source}`);
    }

    const catalogSdk = new ClpSdk(fetcher)
      .useCatalog(catalogId)
      .useApiOptions({ version: draft ? 'draft' : 'active', link, configurationId, projectId })
      .build();
    const productIdNumber = productId;

    const apiPromise = composite
      ? catalogSdk.loadCompositeProduct(productIdNumber)
      : catalogSdk.loadBasicProduct(productIdNumber);

    const view3dPromise = (
      composite ? fetcher.getCompositeView3dSpecification(params) : fetcher.getProductView3dSpecification(params)
    ).catch((error) => {
      console.error(`Cannot load view3d.json for product ${productId}. Using fallback value`);
      console.error(error);
      return view3dFallBack;
    });

    const uiPromise = (
      composite ? fetcher.getCompositeUiSpecification(params) : fetcher.getProductUiSpecification(params)
    ).catch((_error) => {
      console.error(`Cannot load ui.json for product ${productId}. Using fallback value`);
      return uiFallBack;
    });

    Promise.all([apiPromise, view3dPromise, uiPromise])
      .then(([api, view3d, configuratorUI]) => {
        setLoadConfigurationError(null);
        setState({
          params,
          sdk: { composite, api } as Sdk,
          projectId,
          draft,
          productId: productIdNumber,
          configuratorUI: configuratorUI,
          // The fetcher declares `IView3dSpecificationDto`, which clp-api-types defines as
          // the opaque `Record<string, never>`; what it actually resolves is the parsed
          // view3d document that the viewer consumes.
          view3d: view3d as View3dConfigurationDto,
          hideConfigurationControls,
          fetcher,
          without3d: hide3d,
        });
      })
      .catch((error) => {
        const cause = (error as any).cause;
        let causeMessage: string | undefined;
        if (cause && typeof cause === 'object' && 'message' in cause && 'appErrorCode' in cause && 'scope' in cause) {
          causeMessage = cause.message;
        }
        setLoadConfigurationError({ code: error.message, cause: causeMessage });
        console.error(error);
        setState(null);
      });
  }, [link]);

  // ADR-0045 v5: seed the root DisplayContext from the loaded SDK API. Composite
  // products expose `productType.productTypeKey` directly via `getProduct()`;
  // basic products do not yet plumb the PT key through the configurator data
  // path, so we leave the basic-mode context empty until that gap is closed.
  // TODO(ADR-0045): once `IBasicProductApi` exposes `bptKey`/`bprodKey`, set them here.
  const rootDisplayContext = useMemo<DisplayContext>(() => {
    if (!state) return {};
    if (state.sdk.composite) {
      const api = state.sdk.api;
      const cptKey = api.getProduct?.()?.productType?.productTypeKey;
      const cprodKey = api.getProductKey?.();
      const ctx: DisplayContext = {};
      if (cptKey) ctx.cptKey = cptKey;
      if (cprodKey) ctx.cprodKey = cprodKey;
      return ctx;
    }
    return {};
  }, [state]);

  const content = (
    <DisplayContextProvider value={rootDisplayContext}>
      <ConfiguratorContext.Provider value={state}>
        <Configurator loadConfigurationError={loadConfigurationError} languageIssue={languageIssue} />
      </ConfiguratorContext.Provider>
    </DisplayContextProvider>
  );

  return canLoadTranslations ? (
    <LocalizationProvider
      client={localizationClient}
      request={{
        productId: productIdNum!,
        projectId,
        catalogId,
        draft,
        include: TRANSLATION_FAMILIES,
        layers: TRANSLATION_LAYERS,
      }}
      language={effectiveRequestedLanguage(requestedLanguage, languageIssue)}
    >
      <ResolvedLanguage />
      <LanguageFallbackGuard
        requestedLanguage={requestedLanguage}
        debugContext={languageDebugContext}
        onLanguageUnavailable={setLanguageIssue}
      />
      {content}
    </LocalizationProvider>
  ) : (
    content
  );
};
